import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WORKER_ROLES } from "@/lib/checklist";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";

export default function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [role, setRole] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!role) {
      toast.error("Please select your role");
      return;
    }
    setBusy(true);
    try {
      const payload = { name, email, password, worker_role: role };
      const trimmedSlug = orgSlug.trim().toLowerCase();
      if (trimmedSlug) payload.org_slug = trimmedSlug;
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      toast.success("Account created", { description: "Your compliance checklist is ready." });
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Registration failed", { description: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <Link to="/" className="inline-flex items-center mb-8" data-testid="register-logo">
          <img
            src="/audentra-logo.png"
            alt="Audentra"
            className="h-12 w-auto"
          />
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: form */}
          <div className="lg:col-span-7">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Create your worker account</h1>
            <p className="text-slate-600 mt-2 max-w-xl">
              This registration is for care workers only. Team members and managers are invited by their organisation.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="label-caps">Full name</Label>
                  <Input
                    id="name"
                    required
                    data-testid="register-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="label-caps">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid="register-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="label-caps">Password (min 6 chars)</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  data-testid="register-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="org-slug" className="label-caps">Workspace code (optional)</Label>
                <Input
                  id="org-slug"
                  data-testid="register-org-slug"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(e.target.value)}
                  placeholder="e.g. sunrise-care-ltd"
                />
                <p className="text-xs text-slate-500">
                  Ask your manager for the workspace code. Leave blank to join the default Audentra workspace.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="label-caps">Select your role</Label>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {WORKER_ROLES.map((r) => {
                    const active = role === r.key;
                    return (
                      <button
                        type="button"
                        key={r.key}
                        onClick={() => setRole(r.key)}
                        data-testid={`role-${r.key}`}
                        className={`text-left rounded-lg border p-3.5 transition ${
                          active
                            ? "border-teal-700 bg-teal-50 ring-2 ring-teal-200"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-heading font-semibold text-slate-900 text-sm">{r.label}</div>
                          {active && <CheckCircle2 className="h-4 w-4 text-teal-700 shrink-0" />}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 leading-relaxed">{r.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button
                type="submit"
                disabled={busy}
                data-testid="register-submit"
                className="h-11 bg-teal-700 hover:bg-teal-800 rounded-full px-8"
              >
                {busy ? "Creating account…" : "Create account"}
              </Button>
            </form>

            <div className="my-6 flex items-center gap-3 text-xs text-slate-500 max-w-md">
              <div className="flex-1 h-px bg-slate-200" /> OR{" "}
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <Button
              variant="outline"
              onClick={googleLogin}
              data-testid="register-google-btn"
              className="h-11 rounded-full border-slate-300 px-6"
            >
              Sign up with Google
            </Button>

            <p className="text-sm text-slate-600 mt-8">
              Already have an account?{" "}
              <Link to="/login" data-testid="link-login" className="text-teal-700 font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          {/* Right: info */}
          <div className="lg:col-span-5">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="label-caps text-slate-500">What happens next</div>
              <ol className="mt-4 space-y-4">
                {[
                  {
                    t: "Personalised checklist",
                    d: "Standard onboarding items plus role-specific certifications appear immediately after you register.",
                  },
                  {
                    t: "Upload once",
                    d: "Add PDFs or photos for each item. Your compliance team is notified automatically.",
                  },
                  {
                    t: "Get feedback fast",
                    d: "If anything is rejected, you'll see the reason and can re-upload right away.",
                  },
                ].map((s, i) => (
                  <li key={s.t} className="flex gap-3">
                    <div className="h-7 w-7 rounded-full bg-teal-700 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{s.t}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
